import { CheckCircleOutlined, DollarOutlined, EditOutlined, EyeOutlined, LockOutlined, PhoneOutlined, PoweroffOutlined, ShopOutlined, UnlockOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Avatar, Badge, Button, Card, Col, DatePicker, Descriptions, Form, Input, Modal, Row, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { useContext, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import dayjs from '../config/dayjs';
import { AuthContext } from '../context/auth.context';
import { Device, DeviceMetadata } from '../types/device.types';
import { formatNumber, handleAPIError } from '../utils/helpers';
import axios from '../config/axios';
import CopyableText from './copyable-text';
import { ContractAmendmentActions } from './contract-amendments';
import SwapDeviceModal from './swap-device-modal';

const { Title, Text } = Typography;

interface DeviceDetailsProps {
	device: Device;
}

export const DeviceContractInfo = ({ device }: DeviceDetailsProps) => {
	const { user } = useContext(AuthContext);
	const isSuperAdmin = user?.is_superuser || user?.admin_type === 'super_admin';
	const canManageDevice = isSuperAdmin || user?.admin_type === 'support_admin';
	const queryClient = useQueryClient();
	const [imeiModalOpen, setImeiModalOpen] = useState(false);
	const [imeiForm] = Form.useForm<{ imei: string }>();
	const [isRealtimeModalOpen, setIsRealtimeModalOpen] = useState(false);
	const [leaseModalOpen, setLeaseModalOpen] = useState(false);
	const [leaseDate, setLeaseDate] = useState<Dayjs | null>(null);
	const [writeOffOpen, setWriteOffOpen] = useState(false);
	const [writeOffForm] = Form.useForm<{ reason: string }>();
	const [swapInOpen, setSwapInOpen] = useState(false);

	const deviceAction = useMutation({
		mutationFn: async (payload: { action: string; epoch_ms?: number }) => {
			await axios.post(`/admin/devices/${device.imei}/action`, payload, { withCredentials: true });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['device-metadata', device.id] });
			queryClient.invalidateQueries({ queryKey: ['device-action-logs', device.imei] });
			message.success('Action sent successfully');
		},
		onError: handleAPIError,
	});

	const { data: actionLogs = [] } = useQuery<{ action: string; admin: string; created_at: string }[]>({
		queryKey: ['device-action-logs', device.imei],
		queryFn: async () => {
			const res = await axios.get(`/admin/devices/${device.imei}/action-logs`, { withCredentials: true });
			return res.data.results;
		},
		enabled: !!device.imei && canManageDevice,
	});

	const updateImei = useMutation({
		mutationFn: async (newImei: string) => {
			await axios.patch(
				`/admin/super/devices/${device.id}/imei`,
				{ imei: newImei },
				{ withCredentials: true },
			);
		},
		onSuccess: () => {
			setImeiModalOpen(false);
			imeiForm.resetFields();
			queryClient.invalidateQueries({ queryKey: ['device', device.id] });
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			queryClient.invalidateQueries({ queryKey: ['device-metadata', device.id] });
		},
		onError: handleAPIError,
	});

	const approveHeldDevice = useMutation({
		mutationFn: async () => {
			await axios.post(`/admin/super/devices/${device.id}/approve`, {}, { withCredentials: true });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['device', device.id] });
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			message.success('Device approved successfully');
		},
		onError: handleAPIError,
	});

	const markForSwap = useMutation({
		mutationFn: async () => {
			await axios.post(`/admin/super/devices/${device.id}/mark-for-swap`, {}, { withCredentials: true });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['device', device.id] });
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			message.success('Device marked for swap');
		},
		onError: handleAPIError,
	});

	const writeOff = useMutation({
		mutationFn: async (reason: string) => {
			const res = await axios.post(
				`/admin/super/devices/${device.id}/write-off`,
				{ reason },
				{ withCredentials: true },
			);
			return res.data.results;
		},
		onSuccess: (data) => {
			setWriteOffOpen(false);
			writeOffForm.resetFields();
			queryClient.invalidateQueries({ queryKey: ['device', device.id] });
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			message.success(`Written off: UGX ${formatNumber(Number(data.written_off_amount))}`);
		},
		onError: handleAPIError,
	});

	const reverseWriteOff = useMutation({
		mutationFn: async () => {
			await axios.post(
				`/admin/super/devices/${device.id}/write-off/reverse`,
				{},
				{ withCredentials: true },
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['device', device.id] });
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			message.success('Back in collection');
		},
		onError: handleAPIError,
	});

	// Fetch device metadata from API
	const {
		data: deviceMetadata,
		isLoading: metadataLoading,
		error: metadataError,
	} = useQuery({
		queryKey: ['device-metadata', device?.id],
		queryFn: async () => {
			const response = await axios.get(`/admin/devices/${device.imei}/metadata`, {
				withCredentials: true,
			});
			return response.data.results;
		},
		enabled: !!device?.id,
	});

	if (!device) {
		return <div>No device found</div>;
	}

	const getStatusColor = (status: Device['status']) => {
		switch (status) {
			case 'pending':
				return 'gold';
			case 'approved':
				return 'green';
			case 'rejected':
				return 'red';
			case 'pending_swap':
				return 'orange';
			default:
				return 'default';
		}
	};

	const statusDisplayName = (status: string) => {
		return status && status.length > 0 ? status.charAt(0).toUpperCase() + status.slice(1) : '';
	};

	const getDurationUnit = (unit: string) => {
		return unit === 'month' ? (device.duration > 1 ? 'Months' : 'Month') : device.duration > 1 ? 'Weeks' : 'Week';
	};

	// Parse device metadata (financial details)
	const metadata: DeviceMetadata = typeof device.metadata === 'string' ? JSON.parse(device.metadata) : device.metadata;

	// Use API metadata for device status, subscription, and realtime stats
	const apiMetadata = deviceMetadata || {};

	const formatRealtimeStatValue = (key: string, value: unknown): string => {
		if (value === null || value === undefined) return 'N/A';

		// Handle specific formatting for certain fields
		if (key.toLowerCase().includes('time') && typeof value === 'number' && value > 1000000000000) {
			return dayjs(value).format('DD MMM YYYY, HH:mm:ss');
		}

		if (key.toLowerCase().includes('bytes') && typeof value === 'number') {
			const mb = value / (1024 * 1024);
			return mb > 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(2)} MB`;
		}

		if (key.toLowerCase().includes('temperature') && typeof value === 'number') {
			return `${value}°C`;
		}

		if (key.toLowerCase().includes('voltage') && typeof value === 'number') {
			return `${value}V`;
		}

		if (key.toLowerCase().includes('percentage') || key.toLowerCase().includes('level')) {
			return `${value}%`;
		}

		if (typeof value === 'boolean') {
			return value ? 'Yes' : 'No';
		}

		if (typeof value === 'object' && Array.isArray(value)) {
			return value.length > 0 ? value.join(', ') : 'None';
		}

		return String(value);
	};

	const formatRealtimeStatKey = (key: string): string => {
		return key
			.replace(/([A-Z])/g, ' $1')
			.replace(/^./, (str) => str.toUpperCase())
			.trim();
	};

	return (
		<div className="space-y-6">
			{/* Hold Reason Alert */}
			{device.hold_reason && (
				<Alert
					type="warning"
					showIcon
					icon={<WarningOutlined />}
					message="Device On Hold"
					description={device.hold_reason}
					action={
						isSuperAdmin && device.status === 'pending' ? (
							<Button
								size="small"
								type="primary"
								icon={<CheckCircleOutlined />}
								loading={approveHeldDevice.isPending}
								onClick={() =>
									Modal.confirm({
										title: 'Approve this device?',
										content: 'This will override the hold and approve the device for the customer.',
										okText: 'Approve',
										onOk: () => approveHeldDevice.mutate(),
									})
								}
							>
								Approve
							</Button>
						) : undefined
					}
				/>
			)}

			{/* Header Section */}
			<Card className="shadow-sm">
				<Row
					gutter={[24, 16]}
					align="middle"
				>
					<Col
						span={24}
						lg={12}
					>
						<div className="flex items-center gap-4">
							<PhoneOutlined style={{ fontSize: '36px', color: '#1890ff' }} />
							<div>
								<Title
									level={3}
									style={{ margin: 0 }}
								>
									{device.phone_model}
								</Title>
								<Tag color={getStatusColor(device.status)}>{statusDisplayName(device.status).replace('_', ' ')}</Tag>
								{device.is_completed && <Tag color="green">COMPLETED</Tag>}
								{device.written_off_at && (
									<Tooltip title={device.write_off_reason || undefined}>
										<Tag color="default">WRITTEN OFF</Tag>
									</Tooltip>
								)}
								{isSuperAdmin && device.status === 'approved' && (
									<Button
										size="small"
										type="default"
										danger
										loading={markForSwap.isPending}
										onClick={() =>
											Modal.confirm({
												title: 'Mark device for swap?',
												content: 'The customer will no longer see this device in the app and will be prompted to register a replacement device on their existing account.',
												okText: 'Mark for Swap',
												okButtonProps: { danger: true },
												onOk: () => markForSwap.mutate(),
											})
										}
									>
										Mark for Swap
									</Button>
								)}
							{isSuperAdmin &&
								device.status === 'approved' &&
								!device.is_completed &&
								(device.written_off_at ? (
									<Button
										size="small"
										loading={reverseWriteOff.isPending}
										onClick={() =>
											Modal.confirm({
												title: 'Put this contract back in collection?',
												content: 'Its balance counts as arrears again and penalties resume from today.',
												okText: 'Reverse write-off',
												onOk: () => reverseWriteOff.mutate(),
											})
										}
									>
										Reverse write-off
									</Button>
								) : (
									<Button
										size="small"
										danger
										onClick={() => setWriteOffOpen(true)}
									>
										Write off
									</Button>
								))}
							<ContractAmendmentActions device={device} />
							{isSuperAdmin && device.status === 'pending' && device.replaces && (
								<>
									<Button
										size="small"
										type="primary"
										onClick={() => setSwapInOpen(true)}
									>
										Swap in
									</Button>
									<SwapDeviceModal
										visible={swapInOpen}
										onCancel={() => setSwapInOpen(false)}
										onSuccess={() => {
											setSwapInOpen(false);
											queryClient.invalidateQueries({ queryKey: ['device', device.id] });
											queryClient.invalidateQueries({ queryKey: ['devices'] });
										}}
										oldDeviceId={device.replaces}
										newDeviceId={device.id}
									/>
								</>
							)}
							{(device.replaces || device.replaced_by) && (
								<div className="mt-2">
									{device.replaces && (
										<Tag color="geekblue">
											Replaces <a href={`/admin/devices?device=${device.replaces}`}>#{device.replaces}</a>
										</Tag>
									)}
									{device.replaced_by && (
										<Tag color="geekblue">
											Replaced by <a href={`/admin/devices?device=${device.replaced_by}`}>#{device.replaced_by}</a>
										</Tag>
									)}
								</div>
							)}
							</div>
						</div>
					</Col>
					<Col
						span={24}
						lg={12}
					>
						<div className="flex justify-end">
							<div className="space-y-3 text-right">
								<div>
									<Text type="secondary">Device IMEI</Text>
									<div className="flex items-center justify-end gap-2">
										{device.imei ? <CopyableText text={device.imei} /> : 'N/A'}
										{isSuperAdmin && (
											<Button
												type="text"
												size="small"
												icon={<EditOutlined />}
												onClick={() => {
													imeiForm.setFieldsValue({ imei: device.imei || '' });
													setImeiModalOpen(true);
												}}
											/>
										)}
									</div>
								</div>

								{/* IMEI Edit Modal */}
								<Modal
									open={imeiModalOpen}
									title="Update Device IMEI"
									onCancel={() => { setImeiModalOpen(false); imeiForm.resetFields(); }}
									footer={null}
									width={400}
								>
									<Form
										form={imeiForm}
										layout="vertical"
										onFinish={(values) => updateImei.mutate(values.imei.trim())}
									>
										<Form.Item
											name="imei"
											label="New IMEI"
											rules={[
												{ required: true, message: 'IMEI is required' },
												{ min: 5, message: 'IMEI seems too short' },
											]}
										>
											<Input placeholder="Enter correct IMEI" maxLength={50} />
										</Form.Item>
										<div className="flex justify-end gap-2">
											<Button onClick={() => { setImeiModalOpen(false); imeiForm.resetFields(); }}>
												Cancel
											</Button>
											<Button type="primary" htmlType="submit" loading={updateImei.isPending} danger>
												Update IMEI
											</Button>
										</div>
									</Form>
								</Modal>
								{apiMetadata.device_status && (
									<div>
										<Text type="secondary">Device Status</Text>
										<div>
											<Tag color={apiMetadata.device_status === 'Active' ? 'green' : 'orange'}>{apiMetadata.device_status}</Tag>
										</div>
									</div>
								)}
								{apiMetadata.subscription_expiry_date && (
									<div>
										<Text type="secondary">Subscription Expiry</Text>
										<div>{dayjs(apiMetadata.subscription_expiry_date).format('DD MMM YYYY')}</div>
									</div>
								)}
								{metadataLoading && (
									<div className="flex justify-center">
										<Spin size="small" />
									</div>
								)}
								{metadataError && !metadataLoading && (
									<Tooltip title="FieldX could not find this device — the IMEI may be incorrect">
										<Tag icon={<WarningOutlined />} color="warning" style={{ cursor: 'default' }}>
											IMEI not in FieldX
										</Tag>
									</Tooltip>
								)}
								{apiMetadata.realTimeStats && !metadataLoading && (
									<div>
										<Button
											type="primary"
											icon={<EyeOutlined />}
											onClick={() => setIsRealtimeModalOpen(true)}
											size="small"
										>
											View Realtime Stats
										</Button>
									</div>
								)}
							</div>
						</div>
					</Col>
				</Row>
			</Card>

			{/* Customer Information */}
			<Card
				title="Customer Information"
				className="shadow-sm"
			>
				<div className="mb-4 flex items-center gap-4">
					<Avatar
						src={device.user.dp}
						size={64}
						style={{ backgroundColor: device.user.dp ? undefined : '#1890ff' }}
					>
						{!device.user.dp && device.user.first_name && device.user.last_name
							? `${device.user.first_name.charAt(0) || ''}${device.user.last_name.charAt(0) || ''}`
							: ''}
					</Avatar>
					<div>
						<Title
							level={4}
							style={{ margin: 0 }}
							className="uppercase"
						>
							{device.user.first_name} {device.user.last_name}
						</Title>
						<CopyableText text={device.user.phone_number}>
							<a
								href={`tel:+${device.user.phone_number}`}
								className="text-blue-500"
							>
								{device.user.phone_number}
							</a>
						</CopyableText>
						<div>
							<Tag color="blue">{device.user.status ? device.user.status.toUpperCase() : ''}</Tag>
						</div>
					</div>
				</div>
			</Card>

			{/* Retailer Information */}
			<Card
				title="Retailer Information"
				className="shadow-sm"
			>
				<div className="flex items-center gap-4">
					<ShopOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
					<div>
						<Title
							level={4}
							style={{ margin: 0 }}
						>
							{device.retailer.business_name}
						</Title>
						<Text type="secondary">Vendor ID: {device.retailer.vendor_id}</Text>
					</div>
				</div>

				{device.sold_by && (
					<div className="mt-4 border-t border-gray-200 pt-4">
						<Text
							strong
							className="mb-2 block"
						>
							Sold By:
						</Text>
						<div className="flex items-center gap-4">
							<ShopOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
							<div>
								<Title
									level={5}
									style={{ margin: 0 }}
								>
									{device.sold_by.business_name}
								</Title>
								<Text type="secondary">Vendor ID: {device.sold_by.vendor_id}</Text>
							</div>
						</div>
					</div>
				)}
			</Card>

			{/* Device and Payment Details */}
			<Row gutter={16}>
				<Col
					xs={24}
					md={12}
				>
					<Card
						title="Device Details"
						className="h-full shadow-sm"
					>
						<Descriptions
							bordered
							column={1}
							size="small"
						>
							<Descriptions.Item label="Phone Model">{device.phone_model}</Descriptions.Item>
							<Descriptions.Item label="Phone Amount">{formatNumber(device.phone_amount)} UGX</Descriptions.Item>
							<Descriptions.Item label="Cash in Hand">{formatNumber(device.cash_in_hand)} UGX</Descriptions.Item>
							<Descriptions.Item label="Status">{statusDisplayName(device.status)}</Descriptions.Item>
							{device.approved_at && (
								<Descriptions.Item label="Approved At">{dayjs(device.approved_at).format('DD MMM YYYY')}</Descriptions.Item>
							)}
							{device.rejected_at && (
								<Descriptions.Item label="Rejected At">{dayjs(device.rejected_at).format('DD MMM YYYY')}</Descriptions.Item>
							)}
						</Descriptions>
					</Card>
				</Col>

				<Col
					xs={24}
					md={12}
				>
					<Card
						title="Payment Plan"
						className="h-full shadow-sm"
					>
						<Descriptions
							bordered
							column={1}
							size="small"
						>
							<Descriptions.Item label="Duration">
								{device.duration} {getDurationUnit(device.duration_unit)}
							</Descriptions.Item>
							<Descriptions.Item label="Payment Frequency">
								{device.payment_plan && device.payment_plan.length > 0
									? device.payment_plan.charAt(0).toUpperCase() + device.payment_plan.slice(1)
									: ''}
							</Descriptions.Item>
							<Descriptions.Item label="Number of Installments">{device.number_of_installments}</Descriptions.Item>
							<Descriptions.Item label="Regular Installments">{device.number_of_regular_installments}</Descriptions.Item>
							<Descriptions.Item label="Installments Paid">
								<Badge
									color="green"
									count={device.number_of_installments_paid}
									showZero
								/>{' '}
								of {device.number_of_installments}
							</Descriptions.Item>
						</Descriptions>
					</Card>
				</Col>
			</Row>

			{/* Financial Details */}
			<Card
				title={
					<div className="flex items-center gap-2">
						<DollarOutlined /> Financial Details
					</div>
				}
				className="shadow-sm"
			>
				<Row gutter={[24, 16]}>
					<Col
						xs={24}
						lg={12}
					>
						<Descriptions
							bordered
							column={1}
							size="small"
							className="h-full"
						>
							<Descriptions.Item label="Down Payment Percentage">{device.down_payment_percentage}%</Descriptions.Item>
							<Descriptions.Item label="Down Payment Amount">{formatNumber(device.down_payment_amount)} UGX</Descriptions.Item>
							<Descriptions.Item label="Interest Percentage">{device.interest_percentage}%</Descriptions.Item>
							<Descriptions.Item label="Interest Amount">{formatNumber(device.interest_amount)} UGX</Descriptions.Item>
							<Descriptions.Item label="Zazu Fee">{formatNumber(device.zazu_fee)} UGX</Descriptions.Item>
						</Descriptions>
					</Col>
					<Col
						xs={24}
						lg={12}
					>
						<Descriptions
							bordered
							column={1}
							size="small"
							className="h-full"
						>
							<Descriptions.Item label="Total Amount">
								<Text strong>{formatNumber(device.total_amount_to_be_paid)} UGX</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Total Amount Paid">
								<Text type="success">{formatNumber(device.total_amount_paid)} UGX</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Amount Remaining">
								<Text type="warning">
									{formatNumber(parseFloat(device.total_amount_to_be_paid) - parseFloat(device.total_amount_paid))} UGX
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Total Installment Amount">
								{formatNumber(device.total_installment_amount)} UGX
							</Descriptions.Item>
							<Descriptions.Item label="Per Installment Amount">{formatNumber(device.per_installment_amount)} UGX</Descriptions.Item>
							{device.has_overdue_payment && (
								<Descriptions.Item label="Overdue Status">
									<Text type="danger">Overdue Payment</Text>
								</Descriptions.Item>
							)}
							{parseFloat(device.penalty_amount) > 0 && (
								<Descriptions.Item label="Penalty Amount">
									<Text type="danger">{formatNumber(device.penalty_amount)} UGX</Text>
								</Descriptions.Item>
							)}
						</Descriptions>
					</Col>
				</Row>
			</Card>

			{/* Zazu Fees Breakdown */}
			<Card
				title="Zazu Fees Breakdown"
				className="shadow-sm"
			>
				<Descriptions
					bordered
					column={{ xs: 1, sm: 2, md: 3 }}
					size="small"
				>
					<Descriptions.Item label="Service Fee">{formatNumber(metadata.service_fee_amount)} UGX</Descriptions.Item>
					<Descriptions.Item label="Management Fee Per Month">{formatNumber(metadata.management_fee_per_month)} UGX</Descriptions.Item>
					<Descriptions.Item label="Total Management Fee">{formatNumber(metadata.management_fee_amount)} UGX</Descriptions.Item>
				</Descriptions>
			</Card>

			{/* Timeline */}
			<Card
				title="Timeline"
				className="shadow-sm"
			>
				<Descriptions
					bordered
					column={{ xs: 1, sm: 2 }}
					size="small"
				>
					<Descriptions.Item label="Created At">{dayjs(device.created_at).format('DD MMM YYYY, HH:mm')}</Descriptions.Item>
					<Descriptions.Item label="Last Updated">{dayjs(device.updated_at).format('DD MMM YYYY, HH:mm')}</Descriptions.Item>
					{device.approved_at && (
						<Descriptions.Item label="Approved At">{dayjs(device.approved_at).format('DD MMM YYYY, HH:mm')}</Descriptions.Item>
					)}
					{device.rejected_at && (
						<Descriptions.Item label="Rejected At">{dayjs(device.rejected_at).format('DD MMM YYYY, HH:mm')}</Descriptions.Item>
					)}
				</Descriptions>
			</Card>

			{/* Device Actions */}
			{canManageDevice && (
				<Card title="Device Actions" className="shadow-sm">
					<div className="mb-4 flex flex-wrap gap-2">
						<Button
							icon={<UnlockOutlined />}
							onClick={() => deviceAction.mutate({ action: 'unlock' })}
							loading={deviceAction.isPending && (deviceAction.variables as { action: string })?.action === 'unlock'}
						>
							Unlock Device
						</Button>

						<Button
							icon={<LockOutlined />}
							onClick={() =>
								Modal.confirm({
									title: 'Lock this device?',
									content: 'The device will be locked and the customer will not be able to use it.',
									okText: 'Lock',
									okType: 'danger',
									onOk: () => deviceAction.mutate({ action: 'lock' }),
								})
							}
							loading={deviceAction.isPending && (deviceAction.variables as { action: string })?.action === 'lock'}
						>
							Lock Device
						</Button>

						<Button
							icon={<PoweroffOutlined />}
							onClick={() => setLeaseModalOpen(true)}
						>
							Set Lease Date
						</Button>

					</div>

					{/* Action Logs */}
					{actionLogs.length > 0 && (
						<>
							<div className="mb-2 mt-4 text-xs font-medium uppercase tracking-wide text-gray-400">Action History</div>
							<div className="space-y-1">
								{actionLogs.map((log, i) => {
									const isRecent = (Date.now() - new Date(log.created_at).getTime()) < 3 * 60 * 60 * 1000;
									return (
										<div key={i} className="flex items-center justify-between rounded bg-gray-50 px-3 py-1.5 text-sm">
											<span className="text-gray-700">
												{log.action
													.replace(/ sent for device \S+/, '')
													.replace("Device action '", '')
													.replace("'", '')
													.replace('set_lease_date', 'Set Lease Date')
													.replace('lock', 'Lock Device')
													.replace('unlock', 'Unlock Device')
													.replace('reset', 'Reset Device')}
											</span>
											<div className="flex items-center gap-2">
												<span className="text-gray-400">{log.admin}</span>
												<Tooltip title={dayjs(log.created_at).format('DD MMM YYYY, HH:mm:ss')}>
													<Tag color={isRecent ? 'orange' : 'default'}>
														{dayjs(log.created_at).fromNow()}
													</Tag>
												</Tooltip>
											</div>
										</div>
									);
								})}
							</div>
						</>
					)}
				</Card>
			)}

			{/* Set Lease Date Modal */}
			<Modal
				title="Write off this contract"
				open={writeOffOpen}
				onCancel={() => {
					setWriteOffOpen(false);
					writeOffForm.resetFields();
				}}
				okText="Write off"
				okButtonProps={{ danger: true, loading: writeOff.isPending }}
				onOk={() => writeOffForm.submit()}
				destroyOnClose
			>
				<Form
					form={writeOffForm}
					layout="vertical"
					onFinish={(values) => writeOff.mutate(values.reason)}
				>
					<Form.Item
						name="reason"
						label="Reason"
						rules={[{ required: true, message: 'A reason is required' }]}
					>
						<Input.TextArea
							rows={3}
							maxLength={255}
							showCount
							placeholder="Why collection has been given up on"
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Set Lease End Date"
				open={leaseModalOpen}
				okText="Set Lease Date"
				onOk={() => {
					if (!leaseDate) {
						message.warning('Please select a date');
						return;
					}
					deviceAction.mutate({ action: 'set_lease_date', epoch_ms: leaseDate.valueOf() });
					setLeaseModalOpen(false);
					setLeaseDate(null);
				}}
				onCancel={() => {
					setLeaseModalOpen(false);
					setLeaseDate(null);
				}}
				confirmLoading={deviceAction.isPending}
			>
				<DatePicker
					value={leaseDate}
					onChange={(date) => setLeaseDate(date)}
					style={{ width: '100%' }}
					placeholder="Select lease end date"
				/>
			</Modal>

			{/* Realtime Stats Modal */}
			<Modal
				title={
					<div className="flex items-center gap-2">
						<EyeOutlined />
						Realtime Device Statistics
					</div>
				}
				open={isRealtimeModalOpen}
				onCancel={() => setIsRealtimeModalOpen(false)}
				footer={[
					<Button
						key="close"
						onClick={() => setIsRealtimeModalOpen(false)}
					>
						Close
					</Button>,
				]}
				width={800}
				bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
			>
				{metadataLoading ? (
					<div className="flex justify-center py-8">
						<Spin size="large" />
					</div>
				) : apiMetadata.realTimeStats ? (
					<div className="space-y-4">
						<Descriptions
							bordered
							column={{ xs: 1, sm: 2 }}
							size="small"
						>
							{Object.entries(apiMetadata.realTimeStats)
								.filter(([key]) => key !== 'appUsageData') // Filter out appUsageData
								.map(([key, value]) => (
									<Descriptions.Item
										key={key}
										label={formatRealtimeStatKey(key)}
									>
										{formatRealtimeStatValue(key, value)}
									</Descriptions.Item>
								))}
						</Descriptions>
					</div>
				) : (
					<div className="py-8 text-center">
						<Text type="secondary">No realtime statistics available for this device.</Text>
					</div>
				)}
			</Modal>
		</div>
	);
};
